import React from "react";
import { useTranslation } from "react-i18next";
import { HourglassMedium } from "@phosphor-icons/react";
import {
  AlertDialog, AlertDialogAction, AlertDialogContent,
  AlertDialogDescription, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";

// v0.6.8 — a load test found calculations serialize on a shared lock
// under concurrency (deliberate, for correctness — see db.py's
// settings_cache_lock docstring), producing long silent waits with
// no feedback. This surfaces that wait as an unmissable, centered
// popup (same AlertDialog primitive as Freeze/Supersede confirms)
// instead of a wait the operator can't distinguish from a hang.
// Uses the SAME `job`/`onCancel` the inline CalcProgressCard already
// gets — this is an additional, more prominent surface for one
// specific phase, not a separate polling path.
export const QueuePositionDialog = ({ job, onCancel }) => {
  const { t } = useTranslation();
  const phase = job?.progress?.phase;
  const position = job?.progress?.queue_position;
  const open = phase === "queued_for_lock" && position > 0;

  return (
    <AlertDialog open={open}>
      <AlertDialogContent data-testid="queue-position-dialog" className="bg-white dark:bg-slate-900 max-w-md text-center">
        <AlertDialogHeader className="items-center">
          <HourglassMedium size={40} weight="duotone" className="text-teal-600 dark:text-teal-400 mb-2" />
          <AlertDialogTitle className="text-lg">
            {t("ui.calculate.queue.title", "Waiting for another calculation to finish")}
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-2 text-sm text-slate-600 dark:text-slate-300">
              <p>
                {t("ui.calculate.queue.position_label", "Position in queue:")}{" "}
                <span data-testid="queue-position-count" className="font-bold text-teal-700 dark:text-teal-300 text-base">
                  {position}
                </span>
              </p>
              <p>
                {t("ui.calculate.queue.body",
                   "Only one calculation runs at a time so figures never mix between clients. This will start automatically the moment it's your turn — no action needed.")}
              </p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        {onCancel && (
          <AlertDialogAction
            data-testid="queue-position-cancel"
            onClick={onCancel}
            className="bg-transparent text-slate-500 dark:text-slate-400 border border-slate-300 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800"
          >
            {t("ui.calculate.queue.cancel", "Cancel")}
          </AlertDialogAction>
        )}
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default QueuePositionDialog;
